import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSIONS } from '@wms/shared';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ApiError, apiFetch } from '../../lib/api';
import { useCan } from '../../lib/session';
import type { AlertRule } from '../alerts/types';
import { ExpiryBadge } from '../inventory/expiry-badge';
import { formatQty } from '../inventory/format-qty';
import type { MovementListItem, SearchProductCard } from '../inventory/types';
import type { ProductDetail } from './types';

/**
 * 产品详情"库存 / 阈值 / 历史"三个标签页——M4 时的里程碑占位（"将在 M5/M8
 * 上线后可用"）在 M5/M8 交付后一直没有接上真数据，本文件偿还这笔欠账：
 *   库存：GET /inventory/products/:id/stock（批次 + 位置，与搜索卡片同形状）；
 *   历史：GET /inventory/movements?productId=（触及该产品的单据）；
 *   阈值：作用于本产品的 LOW_STOCK 预警规则（产品专属 + 全局/品类继承）。
 */

// ---------------------------------------------------------------------------
// 库存
// ---------------------------------------------------------------------------

export function ProductInventoryTab({
  product,
}: {
  readonly product: ProductDetail;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { data, isPending } = useQuery({
    queryKey: ['products', product.id, 'stock'],
    queryFn: () => apiFetch<SearchProductCard>(`/inventory/products/${product.id}/stock`),
  });

  if (isPending || !data) return <p>{t('app.loading')}</p>;

  return (
    <div>
      <div className="row" style={{ gap: 'var(--space-5)', marginBottom: 'var(--space-3)' }}>
        {(
          [
            ['stock.onHand', data.onHandBase],
            ['stock.reserved', data.reservedBase],
            ['stock.available', data.availableBase],
          ] as const
        ).map(([labelKey, qty]) => (
          <div key={labelKey}>
            <p className="field-hint" style={{ margin: 0 }}>
              {t(labelKey)}
            </p>
            <p style={{ fontSize: 'var(--text-lg)', fontWeight: 600 }}>
              {formatQty(qty, data.units, data.baseUnitCode)}
            </p>
          </div>
        ))}
      </div>

      {data.batches.length === 0 ? (
        <div className="empty-state">
          <p>{t('search.noStock')}</p>
        </div>
      ) : (
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th>{t('products.stockColBatch')}</th>
                <th>{t('products.stockColLot')}</th>
                <th>{t('products.stockColExpiry')}</th>
                <th>{t('stock.onHand')}</th>
                <th>{t('products.stockColLocations')}</th>
              </tr>
            </thead>
            <tbody>
              {data.batches.map((b) => (
                <tr key={b.batchId}>
                  <td>{b.batchNo}</td>
                  <td>{b.externalLotNo ?? '—'}</td>
                  <td>
                    <ExpiryBadge
                      expirationDate={b.expirationDate}
                      hasExpiration={b.hasExpiration}
                    />
                  </td>
                  <td>{formatQty(b.onHandBase, data.units, data.baseUnitCode)}</td>
                  <td>
                    {b.locations.length === 0
                      ? '—'
                      : b.locations.map((loc) => (
                          <p key={loc.locationId} style={{ margin: 0 }}>
                            {loc.warehouseName} › {loc.pathLabel} ·{' '}
                            {formatQty(loc.onHandBase, data.units, data.baseUnitCode)}
                          </p>
                        ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 历史
// ---------------------------------------------------------------------------

export function ProductHistoryTab({
  product,
}: {
  readonly product: ProductDetail;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const fmt = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' });
  const { data, isPending } = useQuery({
    queryKey: ['products', product.id, 'movements'],
    queryFn: () =>
      apiFetch<{ items: MovementListItem[]; nextCursor: string | null }>(
        `/inventory/movements?limit=50&productId=${product.id}`,
      ),
  });

  if (isPending || !data) return <p>{t('app.loading')}</p>;

  if (data.items.length === 0) {
    return (
      <div className="empty-state">
        <p>{t('products.historyEmpty')}</p>
      </div>
    );
  }

  return (
    <div>
      <div className="table-scroll">
        <table className="table">
          <thead>
            <tr>
              <th>{t('movements.colDoc')}</th>
              <th>{t('movements.colType')}</th>
              <th>{t('movements.colPerformedAt')}</th>
              <th>{t('movements.colStatus')}</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((m) => (
              <tr key={m.id}>
                <td>{m.docNo}</td>
                <td>{t(`movements.type${m.type}`, { defaultValue: m.type })}</td>
                <td>{fmt.format(new Date(m.performedAt))}</td>
                <td>
                  {m.status === 'REVERSED' && (
                    <span className="badge badge--danger">{t('movements.statusReversed')}</span>
                  )}
                  {m.reversalOfMovementId && (
                    <span className="badge badge--info">{t('movements.isReversalBadge')}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p style={{ marginTop: 'var(--space-2)' }}>
        <Link to="/movements" className="btn btn--ghost btn--sm">
          {t('products.historyOpenMovements')}
        </Link>
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 阈值
// ---------------------------------------------------------------------------

/** 作用于该产品的低库存规则拆分：产品专属 vs 全局/品类继承（纯函数，可测） */
export function partitionLowStockRules(
  rules: readonly AlertRule[],
  productId: string,
  categoryId: string | null,
): { productRules: AlertRule[]; inheritedRules: AlertRule[] } {
  const low = rules.filter((r) => r.kind === 'LOW_STOCK');
  return {
    productRules: low.filter((r) => r.scopeType === 'PRODUCT' && r.scopeId === productId),
    inheritedRules: low.filter(
      (r) =>
        r.scopeType === 'GLOBAL' ||
        (r.scopeType === 'CATEGORY' && categoryId !== null && r.scopeId === categoryId),
    ),
  };
}

function ruleQty(rule: AlertRule): string {
  const qty = (rule.config as { qtyBase?: string | number }).qtyBase;
  return qty === undefined ? '?' : String(qty);
}

export function ProductThresholdTab({
  product,
}: {
  readonly product: ProductDetail;
}): React.JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const canManage = useCan(PERMISSIONS.ALERT_RULE_MANAGE);
  const [qty, setQty] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data, isPending } = useQuery({
    queryKey: ['alerts', 'rules'],
    queryFn: () => apiFetch<{ items: AlertRule[] }>('/alerts/rules/list'),
    enabled: canManage,
  });

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['alerts'] });
  };

  const create = useMutation({
    mutationFn: () =>
      apiFetch('/alerts/rules', {
        method: 'POST',
        body: {
          kind: 'LOW_STOCK',
          scopeType: 'PRODUCT',
          scopeId: product.id,
          config: { qtyBase: qty.trim() },
          priority: 'NORMAL',
        },
      }),
    onSuccess: () => {
      setQty('');
      setError(null);
      refresh();
    },
    onError: (err) =>
      setError(
        err instanceof ApiError
          ? t(`errors:${err.code}`, { defaultValue: err.code, ...err.params })
          : String(err),
      ),
  });

  const remove = useMutation({
    mutationFn: (ruleId: string) => apiFetch(`/alerts/rules/${ruleId}`, { method: 'DELETE' }),
    onSuccess: () => refresh(),
  });

  if (!canManage) {
    return (
      <div className="empty-state">
        <p>{t('products.thresholdNoPermission')}</p>
      </div>
    );
  }
  if (isPending || !data) return <p>{t('app.loading')}</p>;

  const { productRules, inheritedRules } = partitionLowStockRules(
    data.items,
    product.id,
    product.categoryId,
  );

  return (
    <div>
      <p className="field-hint" style={{ marginBottom: 'var(--space-3)' }}>
        {t('products.thresholdIntro')}
      </p>

      <h3>{t('products.thresholdProductRules')}</h3>
      {productRules.length === 0 ? (
        <p className="field-hint">{t('products.thresholdNone')}</p>
      ) : (
        productRules.map((rule) => (
          <div key={rule.id} className="row" style={{ gap: 'var(--space-2)', padding: 'var(--space-2) 0' }}>
            <strong>
              {t('alerts.ruleSummaryLowStock', { qty: ruleQty(rule) })} {product.baseUnitCode}
            </strong>
            <span className="field-hint" style={{ margin: 0 }}>
              {formatQty(ruleQty(rule), product.units, product.baseUnitCode)}
            </span>
            {!rule.active && <span className="badge badge--muted">{t('alerts.ruleInactive')}</span>}
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={remove.isPending}
              onClick={() => remove.mutate(rule.id)}
            >
              {t('app.delete')}
            </button>
          </div>
        ))
      )}

      <form
        className="row"
        style={{ gap: 'var(--space-2)', alignItems: 'flex-end', marginTop: 'var(--space-2)' }}
        onSubmit={(e) => {
          e.preventDefault();
          if (qty.trim()) create.mutate();
        }}
      >
        <label className="field" style={{ maxWidth: '220px' }}>
          <span>{t('products.thresholdQtyLabel', { unit: product.baseUnitCode })}</span>
          <input
            inputMode="numeric"
            pattern="\d*"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            placeholder="0"
          />
        </label>
        <button
          type="submit"
          className="btn btn--primary btn--sm"
          disabled={!/^\d+$/.test(qty.trim()) || create.isPending}
        >
          {t('products.thresholdAdd')}
        </button>
      </form>
      <p className="field-hint">{t('alerts.ruleThresholdHint')}</p>
      {error && <p className="field-error">{error}</p>}

      {inheritedRules.length > 0 && (
        <div className="section">
          <h3>{t('products.thresholdInheritedRules')}</h3>
          {inheritedRules.map((rule) => (
            <p key={rule.id} style={{ margin: 'var(--space-1) 0' }}>
              {t(`alerts.ruleScope${rule.scopeType}`)} ·{' '}
              {t('alerts.ruleSummaryLowStock', { qty: ruleQty(rule) })}
              {!rule.active && (
                <span className="badge badge--muted" style={{ marginLeft: 'var(--space-2)' }}>
                  {t('alerts.ruleInactive')}
                </span>
              )}
            </p>
          ))}
        </div>
      )}

      <p style={{ marginTop: 'var(--space-3)' }}>
        <Link to="/alerts" className="btn btn--ghost btn--sm">
          {t('products.thresholdOpenAlerts')}
        </Link>
      </p>
    </div>
  );
}
